import {
  Badge,
  Button,
  Description,
  FootprintButton,
  Form,
  Header,
  Input,
  Link,
  Modal,
  Text,
  ValidationStates
} from '@opengov/component-library/dist';
import { BasicColumnTypes } from '@opengov/ogql-types';

import {
  EReportTargetComparisonOperators,
  EReportTargetValueTypes,
  IReportTarget,
  IReportTargetCondition,
  IReportTargetStatus,
  IReportTargetValue
} from '@opengov/reporting-types';
import classnames from 'classnames2';
import compact from 'lodash/compact';
import React from 'react';

import { IReportLocale } from '../../../locale';

import styles from './SetTargetModal.scss';
import { TargetConditionInput } from './TargetConditionInput';

export interface ISetTargetModalProps {
  reportLocaleText: IReportLocale;
  showColumnType: BasicColumnTypes;
  localeFormatter: any;
  targetCondition: IReportTargetCondition;
  secondTargetCondition: IReportTargetCondition;
  targetElseStatus: IReportTargetStatus;
  isEditingTarget: boolean;
  updateConditionOperator(operator: EReportTargetComparisonOperators): void;
  updateConditionValue(value: IReportTargetValue): void;
  updateConditionStatusText(text: string): void;
  updateSecondConditionValue(value: IReportTargetValue): void;
  updateSecondConditionStatusText(text: string): void;
  updateElseStatusText(text: string): void;
  onClickRemove(): void;
  onClose(): void;
  onSave(target: IReportTarget): void;
}

export interface ISetTargetModalState {
  showSecondCondition: boolean;
  conditionValueTextValidationState: ValidationStates;
  conditionStatusTextValidationState: ValidationStates;
  elseConditionStatusTextValidationState: ValidationStates;
  secondConditionValueTextValidationState: ValidationStates;
  secondConditionStatusTextValidationState: ValidationStates;
  secondConditionValueValidationState: ValidationStates;
  secondConditionValueValidationErrorMessage?: string;
}

const currencyRegex = /[$¢€,]+/g;

export const floatingPointNumberMath = (value: number) => parseFloat(value.toPrecision(12));

export const formatConditionValueText = (
  value: string,
  localeFormatter: any,
  showColumnType: BasicColumnTypes
): { value: string; isValid: boolean } => {
  const numberValue = parseFloat(value.replace(currencyRegex, ''));
  const isValueTruthy = !isNaN(numberValue) && numberValue !== null;
  if (!isValueTruthy) {
    return { value, isValid: false };
  }

  const formattedNumber = localeFormatter.number({ value: numberValue });

  if (showColumnType === BasicColumnTypes.Percent && numberValue > 0) {
    return { value: `${floatingPointNumberMath(numberValue / 100)}`, isValid: true };
  }

  return { value: formattedNumber, isValid: true };
};

export const validateSecondConditionValue = (
  reportLocaleText: IReportLocale,
  operator: EReportTargetComparisonOperators,
  firstConditionValueText: string,
  secondConditionValueText: string
): { errorMessage: string; isValid: boolean } => {
  const firstConditionNumericValue = parseFloat(firstConditionValueText.replace(currencyRegex, ''));
  const secondConditionNumericValue = parseFloat(secondConditionValueText.replace(currencyRegex, ''));
  const isValueTruthy = !isNaN(secondConditionNumericValue) && secondConditionNumericValue !== null;
  if (!isValueTruthy) {
    return { errorMessage: null, isValid: true };
  }
  switch (operator) {
    case EReportTargetComparisonOperators.greaterThan:
    case EReportTargetComparisonOperators.greaterThanOrEqualTo:
      return secondConditionNumericValue >= firstConditionNumericValue
        ? { errorMessage: reportLocaleText.setTargetModal.validationErrorMessage.greaterThan, isValid: false }
        : { errorMessage: null, isValid: true };
    case EReportTargetComparisonOperators.lessThan:
    case EReportTargetComparisonOperators.lessThanOrEqualTo:
      return secondConditionNumericValue <= firstConditionNumericValue
        ? { errorMessage: reportLocaleText.setTargetModal.validationErrorMessage.lessThan, isValid: false }
        : { errorMessage: null, isValid: true };
    default:
      return { errorMessage: null, isValid: true };
  }
};

const targetValueTypeFromColumnType = (columnType: BasicColumnTypes) => {
  switch (columnType) {
    case BasicColumnTypes.Currency:
      return EReportTargetValueTypes.currency;
    case BasicColumnTypes.Percent:
      return EReportTargetValueTypes.percent;
    default:
      return EReportTargetValueTypes.number;
  }
};

export class SetTargetModal extends React.Component<ISetTargetModalProps, ISetTargetModalState> {
  constructor(props: ISetTargetModalProps) {
    super(props);
    const hasSecondCondition = !!(
      props.secondTargetCondition &&
      this.props.secondTargetCondition.value.text &&
      this.props.secondTargetCondition.status.text
    );
    this.state = {
      showSecondCondition: hasSecondCondition,
      conditionValueTextValidationState: ValidationStates.None,
      conditionStatusTextValidationState: ValidationStates.None,
      elseConditionStatusTextValidationState: ValidationStates.None,
      secondConditionValueTextValidationState: ValidationStates.None,
      secondConditionStatusTextValidationState: ValidationStates.None,
      secondConditionValueValidationState: ValidationStates.None,
      secondConditionValueValidationErrorMessage: null
    };
  }

  componentDidUpdate(prevProps: ISetTargetModalProps) {
    if (
      this.props.secondTargetCondition !== prevProps.secondTargetCondition ||
      this.props.targetCondition !== prevProps.targetCondition
    ) {
      const { isValid, errorMessage } = validateSecondConditionValue(
        this.props.reportLocaleText,
        this.props.targetCondition.operator,
        this.props.targetCondition.value.text,
        this.props.secondTargetCondition.value.text
      );
      this.setState({
        secondConditionValueValidationState: isValid ? ValidationStates.None : ValidationStates.Error,
        secondConditionValueValidationErrorMessage: isValid ? null : errorMessage
      });
    }
  }

  handleSave = () => {
    const { targetCondition, secondTargetCondition, targetElseStatus } = this.props;
    const conditions = compact([targetCondition, this.state.showSecondCondition && secondTargetCondition]);
    const target: IReportTarget = {
      conditions,
      elseStatus: targetElseStatus
    };
    this.props.onSave(target);
  };

  handleConditionValueChange = (value: string) => {
    const { value: conditionValueText, isValid } = formatConditionValueText(
      value,
      this.props.localeFormatter,
      this.props.showColumnType
    );
    this.setState({
      conditionValueTextValidationState: isValid ? ValidationStates.None : ValidationStates.Error
    });
    this.props.updateConditionValue({
      valueType: targetValueTypeFromColumnType(this.props.showColumnType),
      text: conditionValueText
    });
  };

  handleSecondConditionValueChange = (value: string) => {
    const { value: conditionValueText, isValid } = formatConditionValueText(
      value,
      this.props.localeFormatter,
      this.props.showColumnType
    );
    this.setState({
      secondConditionValueTextValidationState: isValid ? ValidationStates.None : ValidationStates.Error
    });
    this.props.updateSecondConditionValue({
      valueType: targetValueTypeFromColumnType(this.props.showColumnType),
      text: conditionValueText
    });
  };

  handleAddSecondCondition = () => {
    this.setState({
      showSecondCondition: true
    });
  };

  handleRemoveSecondCondition = () => {
    this.setState({
      showSecondCondition: false
    });
    this.props.updateSecondConditionValue({
      valueType: null,
      text: ''
    });
    this.props.updateSecondConditionStatusText('');
  };

  canShowAddSecondConditionButton = (): boolean => {
    return (
      this.props.targetCondition.operator &&
      this.props.targetCondition.operator !== EReportTargetComparisonOperators.equalTo &&
      this.props.targetCondition.operator !== EReportTargetComparisonOperators.notEqualTo &&
      this.props.targetCondition.value.text &&
      this.state.conditionValueTextValidationState !== ValidationStates.Error &&
      this.props.targetCondition.status.text &&
      this.state.conditionStatusTextValidationState !== ValidationStates.Error
    );
  };

  canRenderSecondCondition = (): boolean => {
    return (
      this.props.targetCondition.operator &&
      this.props.targetCondition.operator !== EReportTargetComparisonOperators.equalTo &&
      this.props.targetCondition.operator !== EReportTargetComparisonOperators.notEqualTo
    );
  };

  handleOnChangeOperator = (operator: EReportTargetComparisonOperators) => {
    if (
      operator === EReportTargetComparisonOperators.equalTo ||
      operator === EReportTargetComparisonOperators.notEqualTo
    ) {
      this.handleRemoveSecondCondition();
    }

    this.props.updateConditionOperator(operator);
  };

  canSave = (): boolean => {
    const hasValidElseStatus = !!this.props.targetElseStatus.text && this.props.targetElseStatus.text.trim().length > 0;
    const hasValidFirstCondition =
      this.props.targetCondition.operator &&
      this.props.targetCondition.value.text &&
      this.state.conditionValueTextValidationState !== ValidationStates.Error &&
      this.props.targetCondition.status.text &&
      this.props.targetCondition.status.text.trim().length > 0 &&
      this.state.conditionStatusTextValidationState !== ValidationStates.Error;
    if (this.state.showSecondCondition) {
      const hasValidSecondCondition =
        this.state.secondConditionValueTextValidationState !== ValidationStates.Error &&
        this.props.secondTargetCondition.status.text &&
        this.props.secondTargetCondition.status.text.trim().length > 0 &&
        this.state.secondConditionStatusTextValidationState !== ValidationStates.Error &&
        this.state.secondConditionValueValidationState !== ValidationStates.Error;
      return hasValidFirstCondition && hasValidSecondCondition && hasValidElseStatus;
    }
    return hasValidFirstCondition && hasValidElseStatus;
  };

  renderSecondCondition = () => {
    const { reportLocaleText, showColumnType } = this.props;
    let secondTargetConditionValue =
      this.props.secondTargetCondition.value.text &&
      parseFloat(this.props.secondTargetCondition.value.text.replace(currencyRegex, ''));

    if (showColumnType === BasicColumnTypes.Percent && secondTargetConditionValue > 0) {
      secondTargetConditionValue = floatingPointNumberMath(secondTargetConditionValue * 100);
    }

    return (
      <div className={styles.condition}>
        <Header variant="h4">{this.props.reportLocaleText.setTargetModal.secondConditionDescription}</Header>
        <TargetConditionInput
          fieldName="second-condition"
          isConditionEnabled={false}
          reportLocaleText={this.props.reportLocaleText}
          showColumnType={this.props.showColumnType}
          operator={this.props.targetCondition.operator}
          value={secondTargetConditionValue}
          statusText={this.props.secondTargetCondition.status.text}
          statusPlaceholder={reportLocaleText.setTargetModal.onTrack}
          valueTextValidationErrorMessage={this.state.secondConditionValueValidationErrorMessage}
          valueTextValidationState={
            this.state.secondConditionValueValidationState === ValidationStates.Error ||
            this.state.secondConditionValueTextValidationState === ValidationStates.Error
              ? ValidationStates.Error
              : ValidationStates.None
          }
          statusTextValidationState={this.state.secondConditionStatusTextValidationState}
          onChangeOperator={this.props.updateConditionOperator}
          onChangeValue={this.handleSecondConditionValueChange}
          onChangeStatusText={this.props.updateSecondConditionStatusText}
        />
        <div className={styles.removeStatus}>
          <Text font="condensed" size="small">
            <Link.Quiet
              href="#"
              text={this.props.reportLocaleText.setTargetModal.removeStatus}
              onClick={this.handleRemoveSecondCondition}
              dataTest="set-target-modal-remove-status-btn"
            />
          </Text>
        </div>
      </div>
    );
  };

  renderAddSecondConditionButton = () => {
    return (
      <div className={styles.addStatusControls}>
        <FootprintButton
          variant="dark"
          onClick={this.handleAddSecondCondition}
          dataTest="set-target-modal-add-condition-btn"
          disabled={!this.canShowAddSecondConditionButton()}
        >
          {this.props.reportLocaleText.setTargetModal.addStatus}
        </FootprintButton>
      </div>
    );
  };

  render() {
    const { showColumnType, onClose, reportLocaleText, isEditingTarget, onClickRemove } = this.props;
    let targetConditionValue =
      this.props.targetCondition.value.text &&
      parseFloat(this.props.targetCondition.value.text.replace(currencyRegex, ''));

    if (showColumnType === BasicColumnTypes.Percent && targetConditionValue > 0) {
      targetConditionValue = floatingPointNumberMath(targetConditionValue * 100);
    }

    return (
      <Modal.Work width="wide" onClose={onClose}>
        <div className={styles.header}>
          <Header variant="h2">
            {isEditingTarget ? reportLocaleText.setTargetModal.editTarget : reportLocaleText.setTargetModal.addTarget}
          </Header>
          <Description>{reportLocaleText.setTargetModal.description}</Description>
        </div>
        <div className={styles.body}>
          <div className={styles.condition}>
            <Header variant="h4">{reportLocaleText.setTargetModal.conditionDescription}</Header>
            <TargetConditionInput
              fieldName="first-condition"
              reportLocaleText={reportLocaleText}
              showColumnType={showColumnType}
              operator={this.props.targetCondition.operator}
              value={targetConditionValue}
              statusText={this.props.targetCondition.status.text}
              statusPlaceholder={reportLocaleText.setTargetModal.onTarget}
              valueTextValidationState={this.state.conditionValueTextValidationState}
              statusTextValidationState={this.state.conditionStatusTextValidationState}
              onChangeOperator={this.handleOnChangeOperator}
              onChangeValue={this.handleConditionValueChange}
              onChangeStatusText={this.props.updateConditionStatusText}
            />
          </div>
          {this.canRenderSecondCondition() && this.state.showSecondCondition
            ? this.renderSecondCondition()
            : this.renderAddSecondConditionButton()}
          <div className={styles.condition}>
            <Header variant="h4">{this.props.reportLocaleText.setTargetModal.elseConditionDescription}</Header>
            <div className={classnames(styles.conditionControls, styles.statusControls)}>
              <div className={styles.statusInput}>
                <Form.Item label={reportLocaleText.setTargetModal.status}>
                  <Input.Text
                    placeholder={reportLocaleText.setTargetModal.needsFocus}
                    value={this.props.targetElseStatus.text}
                    onChange={this.props.updateElseStatusText}
                    dataTest="set-target-modal-not-met-status"
                    validationState={this.state.elseConditionStatusTextValidationState}
                  />
                </Form.Item>
              </div>
              <div>
                <Form.Item label={reportLocaleText.setTargetModal.preview}>
                  {this.props.targetElseStatus.text && (
                    <Badge text={this.props.targetElseStatus.text} variant="negative" maxLength={12} />
                  )}
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={styles.saveAndRemoveControls}>
            <div className={styles.removeControls}>
              {isEditingTarget && (
                <Button variant="destructive" onClick={onClickRemove} dataTest="set-target-modal-remove-btn">
                  {reportLocaleText.setTargetModal.remove}
                </Button>
              )}
            </div>
            <div className={styles.saveControls}>
              <Button onClick={onClose} dataTest="set-target-modal-cancel-btn">
                {reportLocaleText.setTargetModal.cancel}
              </Button>
              <Button
                variant="primary"
                onClick={this.handleSave}
                dataTest="set-target-modal-add-btn"
                disabled={!this.canSave()}
              >
                {isEditingTarget ? reportLocaleText.setTargetModal.save : reportLocaleText.setTargetModal.add}
              </Button>
            </div>
          </div>
        </div>
      </Modal.Work>
    );
  }
}
